import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Table,
  Button,
  Dialog,
  Form,
  Input,
  Select,
  Popconfirm,
  Tag,
  Space,
  MessagePlugin,
  Tabs,
  DatePicker,
  NotificationPlugin
} from 'tdesign-react';
import { AddIcon, EditIcon, DeleteIcon, DownloadIcon, UploadIcon } from 'tdesign-icons-react';
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import importTemplate from '../../../assest/imgs/table_exmple.png'
import { supplierArchiveApi } from '../../api';
import type { SupplierArchive } from '../../types';
import dayjs from 'dayjs';
import type { InputValue, NotificationInfoOptions } from 'tdesign-react';
/**
 * 根据入库时间计算有效期和状态
 * @param entryTime 入库时间，支持 Date 对象或日期字符串
 * @returns 包含有效期和状态的对象
 *          - validPeriod: 有效期截止日期（格式：YYYY-MM-DD）
 *          - status: 状态值（1-生效中，2-预警，0-失效）
 */
const calculateStatus = (entryTime: string | Date): { validPeriod: string; status: 1 | 2 | 0 } => {
  let entryDate: Date;
  
  // 处理不同类型的时间输入
  if (entryTime instanceof Date) {
    entryDate = entryTime;
  } else if (typeof entryTime === 'string' && entryTime) {
    entryDate = new Date(entryTime);
  } else {
    throw new Error('无效的时间值');
  }
  
  // 验证时间有效性
  if (isNaN(entryDate.getTime())) {
    throw new Error('无效的时间值');
  }
  
  // 计算有效期：入库时间 + 1年
  const validDate = new Date(entryDate);
  validDate.setFullYear(validDate.getFullYear() + 1);
  const validPeriod = validDate.toISOString().split('T')[0];
  
  // 根据有效期计算状态：
  // - 已过期：状态为 0（失效）
  // - 30天内到期：状态为 2（预警）
  // - 其他：状态为 1（生效中）
  const daysUntilExpire = Math.ceil((validDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
  let status: 1 | 2 | 0 = 1;
  if (daysUntilExpire < 0) {
    status = 0;
  } else if (daysUntilExpire <= 30) {
    status = 2;
  }
  
  return { validPeriod, status };
};

/**
 * 供应商平台管理页面
 * 功能：供应商档案的增删改查、导入导出、预警提醒
 */
export default function SupplierPlatform() {
  // ===================== 列表相关状态 =====================
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]); // 选中的供应商ID列表
  const [isAllSelected, setIsAllSelected] = useState(false); // 是否全选状态
  const [data, setData] = useState<SupplierArchive[]>([]); // 供应商数据列表
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 }); // 分页信息
  const [searchForm] = Form.useForm(); // 搜索表单实例
  const [activeTab, setActiveTab] = useState<Number>(1); // 当前激活的Tab页（1-生效中，2-预警，0-失效）
  
  // ===================== 弹窗相关状态 =====================
  const [dialogVisible, setDialogVisible] = useState(false); // 新增/编辑弹窗可见性
  const [editingSupplier, setEditingSupplier] = useState<SupplierArchive | null>(null); // 正在编辑的供应商数据
  const [importDialogVisible, setImportDialogVisible] = useState(false); // 导入弹窗可见性
  const [importFile, setImportFile] = useState<File | null>(null); // 选中的导入文件
  const [loading, setLoading] = useState(false); // 数据加载状态
  
  // ===================== 导入结果相关状态 =====================
  const [errorDialogVisible, setErrorDialogVisible] = useState(false); // 导入错误弹窗可见性
  const [importErrors, setImportErrors] = useState<string[]>([]); // 导入错误列表
  const [importStats, setImportStats] = useState({ successCount: 0, failCount: 0 }); // 导入统计（成功/失败条数）
 
  // 表单引用，用于表单验证和获取表单值
  const formRef = useRef<any>(null);
  const openNotification = (placement: NotificationInfoOptions['placement']) => {
    NotificationPlugin.warning({
      title: '预警通知',
      content: '当前数据中存在快到期的数据，请点击[预警]按钮进行处理',
      placement,
      duration: 5000,
      offset: [0, 0],
      closeBtn: true,
    });
  };
  /**
   * 获取预警数据
   * 调用后端 API 获取供应商预警信息，包括即将过期的供应商列表
   */
  const loadWarningData = async () => {
    try {
      const res = await supplierArchiveApi.warningReminder();
      if (res.code === 200 && res.data) {
        openNotification('top-right');
      }
    } catch (error) {
      console.error('加载预警数据失败:', error);
    }
  };

  // 使用 useRef 防止 React StrictMode 导致的重复调用
  const loadDataRef = useRef(false);
  const loadWarningRef = useRef(false);

  /**
   * 获取供应商列表数据
   * 根据搜索条件、分页信息和当前Tab页状态查询数据
   * @param pageNum 可选页码参数，不传则使用当前分页状态
   */
  const loadData = useCallback(async (pageNum?: number) => {
    if (loadDataRef.current) {
      loadDataRef.current = false;
      return;
    }
    loadDataRef.current = true;
    
    const targetPage = pageNum ?? pagination.current;
    
    setLoading(true);
    try {
      const searchParams = searchForm.getFieldsValue?.(true) || {};
      const res = await supplierArchiveApi.getPageList({
        pageNum: targetPage,
        pageSize: pagination.pageSize,
        resourceLibraryName: searchParams.resourceLibraryName,
        supplierName: searchParams.supplierName,
        mainBusinessScope: searchParams.mainBusinessScope,
        status: activeTab.toString(), // 根据Tab页过滤状态
      });
      if (res.code === 200) {
        const newData = res.data.list || [];
        setData(newData);
        setPagination(prev => ({ ...prev, total: res.data.total || 0, current: targetPage }));
        
        if (isAllSelected) {
          setSelectedOrderIds(prev => [...new Set([...prev, ...newData.map(item => item.id)])]);
        }
      }
    } catch (error) {
      console.error('加载数据失败:', error);
    } finally {
      setLoading(false);
      loadDataRef.current = false;
    }
  }, [pagination.current, pagination.pageSize, activeTab, searchForm]);

  // 页面加载时获取预警数据
  useEffect(() => {
    if (!loadWarningRef.current) {
      loadWarningRef.current = true;
      loadWarningData();
    }
  }, []);

  // 分页或Tab页切换时重新加载数据（包含首次加载）
  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * 根据搜索条件过滤数据
   * 当前仅处理"是否中标"的额外过滤
   */
  const getFilteredData = () => {
    const searchParams = searchForm.getFieldsValue?.(true) || {};
    return data.filter(item => {
      if (searchParams.alreadyUse !== undefined && searchParams.alreadyUse !== '') {
        const alreadyUseValue = searchParams.alreadyUse === 'true';
        if ((item as any).alreadyUse !== alreadyUseValue) {
          return false;
        }
      }
      return true;
    });
  };

  // 获取过滤后的数据
  const filteredData = getFilteredData();

  // 分页后的数据（当前简化为直接使用过滤后的数据）
  const paginatedData = filteredData;

  /**
   * 表格列配置
   * 定义表格的列结构、渲染方式和交互逻辑
   */
  const columns = [
    {
      colKey: 'selection',
      title: () => (
        <input
          type="checkbox"
          checked={data.length > 0 && data.every(item => selectedOrderIds.includes(item.id))}
          onChange={(e) => {
            if (e.target.checked) {
              setIsAllSelected(true);
              setSelectedOrderIds(data.map((o) => o.id));
            } else {
              setIsAllSelected(false);
              setSelectedOrderIds([]);
            }
          }}
          className="w-4 h-4 rounded border-gray-300"
        />
      ),
      width: 50,
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={selectedOrderIds.includes(row.id)}
          onChange={() => toggleSelectOrder(row.id)}
          className="w-4 h-4 rounded border-gray-300"
        />
      ),
    },
    { colKey: 'resourceLibraryName', title: '资源库名称', ellipsis: true, width: 150 },
    { colKey: 'supplierName', title: '供应商名称', ellipsis: true, width: 150 },
    { colKey: 'libraryUnit', title: '建库单位', ellipsis: true, width: 150 },
    {
      colKey: 'level',
      title: '等级',
      width: 100,
      cell: ({ row }: any) => {
        const levelMap: Record<string, { label: string; theme: string }> = {
          'A': { label: 'A级', theme: 'success' },
          'B': { label: 'B级', theme: 'primary' },
          'C': { label: 'C级', theme: 'default' },
          'A级': { label: 'A级', theme: 'success' },
          'B级': { label: 'B级', theme: 'primary' },
          'C级': { label: 'C级', theme: 'default' },
        };
        const t = levelMap[row.level] || { label: row.level, theme: 'default' };
        return <Tag theme={t.theme as any}>{t.label}</Tag>;
      },
    },
    {
      colKey: 'alreadyUse',
      title: '参与情况',
      width: 180,
      cell: ({ row }: any) => {
        const isWinBidTag = row.alreadyUse == 1 ? { label: '是', theme: 'success' } : { label: '否', theme: 'default' };
        return (
            <div className="flex flex-col text-sm">
                <span className="text-gray-800 truncate">中标: {isWinBidTag.label}</span>
                <span className="text-gray-600 truncate">活跃度: {row.tenderVal}</span>
            </div>
        );
      },
    },
    { colKey: 'mainBusinessScope', title: '主营范围', ellipsis: true, width: 150 },
    { 
      colKey: 'type', 
      title: '供应商类型', 
      width: 120,
      cell: ({ row }: any) => {
        return <Tag>{row.type}</Tag>;
      },
     },
    { 
      colKey: 'storageMethod', 
      title: '入库方式', 
      width: 120,
      cell: ({ row }: any) => {
        return <Tag>{row.storageMethod}</Tag>;
      },
     },
    { 
      colKey: 'storageTime', 
      title: '入库时间', 
      width: 130,
      cell: ({ row }: any) => {
        return row.storageTime ? dayjs(row.storageTime).format('YYYY-MM-DD') : '-';
      },
    },
    { 
      colKey: 'validityPeriod', 
      title: '有效期', 
      width: 130,
      cell: ({ row }: any) => {
        return row.validityPeriod ? dayjs(row.validityPeriod).format('YYYY-MM-DD') : '-';
      },
    },
    { 
      colKey: 'applicableScope', 
      title: '适用范围', 
      width: 120,
      cell: ({ row }: any) => {
        return <Tag>{row.applicableScope}</Tag>;
      },
     },
    {
      colKey: 'status',
      title: '状态',
      width: 100,
      cell: ({ row }: any) => {
        const statusMap: Record<string, { label: string; theme: string }> = {
          '1': { label: '生效中', theme: 'success' },
          '2': { label: '预警', theme: 'warning' },
          '0': { label: '失效', theme: 'default' },
        };
        const t = statusMap[row.status] || { label: '未知', theme: 'default' };
        return <Tag theme={t.theme as any}>{t.label}</Tag>;
      },
    },
    {
      colKey: 'action',
      title: '操作',
      width: 200,
      fixed: 'right',
      cell: ({ row }: any) => (
        <Space>
          <Button theme="success" variant="text" icon={<EditIcon />} onClick={() => handleEdit(row)}>编辑</Button>
          <Popconfirm content="确定删除该供应商吗？" onConfirm={() => handleDelete(row.id)}>
            <Button theme="danger" variant="text" icon={<DeleteIcon />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  /**
   * 切换选中状态
   * @param orderId 供应商ID
   */
  const toggleSelectOrder = (orderId: number) => {
    setSelectedOrderIds((prev) => {
      if (prev.includes(orderId)) {
        setIsAllSelected(false);
        return prev.filter((id) => id !== orderId);
      }
      return [...prev, orderId];
    });
  };

  /**
   * 处理搜索
   * 重置分页到第一页并重新加载数据
   */
  const handleSearch = () => {
    loadData(1);
  };

  /**
   * 处理重置
   * 清空搜索表单、重置分页并重新加载数据
   */
  const handleReset = () => {
    searchForm.reset();
    loadData(1);
  };

  /**
   * 重置表单状态
   * 清除编辑数据并重置表单
   */
  const resetFormState = () => {
    setEditingSupplier(null);
    if (formRef.current) {
      formRef.current.reset();
    }
  };

  /**
   * 处理新增
   * 重置表单状态并打开弹窗
   */
  const handleAdd = () => {
    resetFormState()
    setDialogVisible(true);
  };

  /**
   * 处理编辑
   * 根据ID获取供应商详情并打开编辑弹窗
   * @param row 选中的供应商数据
   */
  const handleEdit = async (row: SupplierArchive) => {
    let res = await supplierArchiveApi.getById(row.id);
    if (res.code === 200) {
      setEditingSupplier(res.data);
      setDialogVisible(true);
    }
  };

  /**
   * 处理删除
   * 删除指定供应商并刷新列表
   * @param id 供应商ID
   */
  const handleDelete = async (id: number) => {
    try {
      const res = await supplierArchiveApi.delete(id);
      if (res.code === 200) {
        MessagePlugin.success('删除成功');
        setSelectedOrderIds([]);
        setIsAllSelected(false);
        loadData();
      }
    } catch (error) {
      console.error('删除失败:', error);
      MessagePlugin.error('删除失败');
    }
  };

  /**
   * 处理批量删除
   * 删除选中的多个供应商并刷新列表
   */
  const handleBatchDelete = async () => {
    if (selectedOrderIds.length === 0) {
      MessagePlugin.warning('请先选择要删除的记录');
      return;
    }
    
    try {
      const res = await supplierArchiveApi.deleteBatch(selectedOrderIds);
      if (res.code === 200) {
        MessagePlugin.success('批量删除成功');
        setSelectedOrderIds([]);
        setIsAllSelected(false);
        console.log(selectedOrderIds,'res')
        loadData(1);
      }
    } catch (error) {
      console.error('批量删除失败:', error);
      MessagePlugin.error('批量删除失败');
    }
  };

  /**
   * 处理表单提交（新增/编辑）
   * 验证表单、计算有效期和状态、提交数据
   */
  const handleSubmit = async () => {
    try {
       const result = await formRef.current?.validate();
      if (result !== true) {
        return;
      }
      const values = formRef.current?.getFieldsValue(true) || {};
      
      // 处理入库时间格式
      let storageTime = values.storageTime;
      if (storageTime && typeof storageTime === 'object' && !isNaN(storageTime.getTime?.())) {
        storageTime = storageTime.toISOString().split('T')[0];
      } else if (typeof storageTime !== 'string') {
        throw new Error('入库时间格式不正确');
      }
      
      // 计算有效期和状态
      const { validPeriod, status } = calculateStatus(storageTime);
      let queryParams = {
        ...values,
        storageTime: storageTime + ' 00:00:00',
        validityPeriod: validPeriod + ' 00:00:00',
        status,
      };
      
      // 根据是否有编辑数据判断是新增还是修改
      if (editingSupplier) {
        const res = await supplierArchiveApi.update(editingSupplier.id, queryParams);
        if (res.code === 200) {
          MessagePlugin.success('修改成功');
          setDialogVisible(false);
          loadData();
        }
      } else {
        const res = await supplierArchiveApi.create(queryParams);
        if (res.code === 200) {
          MessagePlugin.success('新增成功');
          setDialogVisible(false);
          loadData();
        }
      }
    } catch (error) {
      console.error('操作失败:', error);
      MessagePlugin.error('操作失败');
    }
  };

  /**
   * 处理导入按钮点击
   * 清空之前的导入文件并打开导入弹窗
   */
  const handleImport = () => {
    setImportFile(null);
    setImportDialogVisible(true);
    
    const fileInput = document.getElementById('import-file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  /**
   * 处理导出
   * 调用后端 API 导出供应商数据为 Excel 文件
   */
  const handleExport = async () => {
    try {
      const searchParams = searchForm.getFieldsValue?.(true) || {};
      
      const params: { resourceLibraryName?: string; status?: number; supplierName?: string } = {};
      if (searchParams.resourceLibraryName) {
        params.resourceLibraryName = searchParams.resourceLibraryName;
      }
      if (searchParams.supplierName) {
        params.supplierName = searchParams.supplierName;
      }
      if (activeTab !== undefined && activeTab !== null) {
        params.status = Number(activeTab);
      }
      
      const blob = await supplierArchiveApi.exportSupplierArchive(params);
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = '供应商数据.xlsx';
      link.click();
      
      MessagePlugin.success('导出成功');
      setSelectedOrderIds([]);
      setIsAllSelected(false);
    } catch (error) {
      console.error('导出失败:', error);
      MessagePlugin.error('导出失败');
    }
  };

  /**
   * 处理下载导入模板
   * 生成包含下拉选择的Excel模板文件
   */
  const handleDownloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('供应商导入模板')

    const headers = [
      '资源库名称',
      '供应商名称',
      '建库单位',
      '初始等级',
      '是否中标',
      '投标活跃度',
      '主营范围',
      '供应商类型',
      '入库方式',
      '入库时间'
    ]

    worksheet.addRow(headers)

    // 设置表头样式
    const headerRow = worksheet.getRow(1)
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD9E2F3' }
      }
      cell.font = {
        bold: true,
        color: { argb: 'FF333333' }
      }
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      }
    })

    // 设置列宽
    worksheet.columns = [
      { width: 20 },
      { width: 25 },
      { width: 20 },
      { width: 12 },
      { width: 12 },
      { width: 12 },
      { width: 20 },
      { width: 15 },
      { width: 18 },
      { width: 15 }
    ]

    // 定义特定列的下拉选择配置
    const dropdownConfigs: Record<number, string[]> = {
      4: ['A', 'B', 'C'],                           // 初始等级
      5: ['是', '否'],                              // 是否中标
      8: ['生产商', '代理商', '其他'],               // 供应商类型
      9: ['自主寻源', '推荐引入', '公开征集']        // 入库方式
    }

    // 为每一行设置边框和下拉选择
    for (let row = 2; row <= 9999; row++) {
      for (let col = 1; col <= 10; col++) {
        const cell = worksheet.getCell(row, col)
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }

        if (dropdownConfigs[col]) {
          cell.dataValidation = {
            type: 'list',
            allowBlank: true,
            formulae: [`"${dropdownConfigs[col].join(',')}"`],
            showErrorMessage: true,
            error: '请选择下拉列表中的值'
          }
        }
      }
    }

    try {
      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      saveAs(blob, '供应商导入模板.xlsx')
      MessagePlugin.success('模板下载成功')
    } catch (error) {
      console.error('生成模板失败:', error)
      MessagePlugin.error('生成模板失败')
    }
  };

  /**
   * 处理导入文件选择
   * 验证文件格式和大小，设置选中文件
   * @param e 文件选择事件
   */
  const handleImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    console.log(files,'files',e)
    if (files.length === 0) return;
    const file = files[0];
    
    // 验证文件格式
    if (!file.name.toLowerCase().endsWith('.csv') && !file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
      alert('仅支持CSV、XLS、XLSX格式文件');
      return;
    }
    
    // 验证文件大小（不超过10MB）
    if (file.size > 10 * 1024 * 1024) {
      alert('文件大小不能超过10MB');
      return;
    }
    
    setImportFile(file);
  };

  /**
   * 处理导入提交
   * 使用FormData方式上传文件，处理导入结果
   */
  const handleImportSubmit = async () => {
    if (!importFile) {
      MessagePlugin.warning('请先选择文件');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await supplierArchiveApi.importSupplierArchive(formData);
      console.log(res,'res')
      
      // 判断是否有导入错误
      if (res.data?.errorList && res.data.errorList.length > 0) {
        setImportErrors(res.data.errorList);
        setImportStats({ 
          successCount: res.data.successCount || 0, 
          failCount: res.data.failCount || 0 
        });
        setErrorDialogVisible(true);
      } else {
        MessagePlugin.success(`导入完成，成功导入 ${res.data?.successCount || 0} 条`);
      }
      // 清理状态并刷新数据
      setImportFile(null);
      setImportDialogVisible(false);
      loadData(1);
    } catch (err: any) {
      alert(err.message || '导入失败');
    }
  };

  return (
    <div >
      
      {/* 页面标题和操作按钮 */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-bold">供应商平台管理</h1>
        <Space>
          <Button icon={<UploadIcon />} onClick={handleImport}>导入</Button>
          <Button 
            icon={<DownloadIcon />} 
            onClick={handleExport}
          >导出</Button>
          <Button 
            icon={<DeleteIcon />} 
            theme="danger"
            onClick={handleBatchDelete}
            disabled={selectedOrderIds.length === 0}
          >批量删除</Button>
          <Button icon={<AddIcon />} theme="primary" onClick={handleAdd}>新增供应商</Button>
        </Space>
      </div>

      {/* 搜索表单 */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
        <Form form={searchForm} layout="inline">
          <Form.FormItem name="resourceLibraryName" label="资源库">
            <Input placeholder="请输入资源库名称" clearable style={{ width: 180 }} />
          </Form.FormItem>
          <Form.FormItem name="supplierName" label="供应商">
            <Input placeholder="请输入供应商名称" clearable style={{ width: 180 }} />
          </Form.FormItem>
          <Form.FormItem name="mainBusinessScope" label="主营范围">
            <Input placeholder="请输入主营范围" clearable style={{ width: 180 }} />
          </Form.FormItem>
          <Form.FormItem>
            <Button theme="primary" onClick={handleSearch}>查询</Button>
            <Button style={{ marginLeft: 8 }} onClick={handleReset}>重置</Button>
          </Form.FormItem>
        </Form>
      </div>

      {/* 状态Tab切换 */}
      <Tabs
        value={activeTab as any}
        onChange={(value) => {
          setActiveTab(value as 1 | 2 | 0);
          setPagination({ current: 1, pageSize: 10, total: 0 });
        }}
      >
        <Tabs.TabPanel label={`生效中`} value={1} />
        <Tabs.TabPanel label={`预警`} value={2} />
        <Tabs.TabPanel label={`失效`} value={0} />
      </Tabs>

      {/* 供应商列表表格 */}
        <Table
          columns={columns as any}
          data={paginatedData}
          loading={loading}
          pagination={{
            ...pagination,
            total: pagination.total,
            onChange: (pageInfo: { current: number; pageSize: number }) => {
              const { current, pageSize } = pageInfo;
              setPagination({ current, pageSize, total: pagination.total });
            },
          }}
          rowKey="id"
        />

      {/* 新增/编辑供应商弹窗 */}
      <Dialog
        header={editingSupplier ? '编辑供应商' : '新增供应商'}
        visible={dialogVisible}
        onClose={() => {
          setDialogVisible(false)
          resetFormState();
        }}
        onConfirm={handleSubmit}
        confirmBtn="保存"
        width={600}
        key={editingSupplier ? `edit-${editingSupplier.id}` : 'add-new'}
      >
        <Form ref={formRef} initialData={editingSupplier || {}}   labelWidth={100}>
          <Form.FormItem label="资源库名称" name="resourceLibraryName" rules={[{ required: true, message: '请输入资源库名称' }]}>
            <Input placeholder="请输入资源库名称" />
          </Form.FormItem>
          <Form.FormItem label="供应商名称" name="supplierName" rules={[{ required: true, message: '请输入供应商名称' }]}>
            <Input placeholder="请输入供应商名称" />
          </Form.FormItem>
          <Form.FormItem label="建库单位" name="libraryUnit" rules={[{ required: true, message: '请输入建库单位' }]}>
            <Input placeholder="请输入建库单位" />
          </Form.FormItem>
          <Form.FormItem label="初始等级" name="level" rules={[{ required: true, message: '请选择初始等级' }]}>
            <Select placeholder="请选择初始等级">
              <Select.Option value="A">A级</Select.Option>
              <Select.Option value="B">B级</Select.Option>
              <Select.Option value="C">C级</Select.Option>
            </Select>
          </Form.FormItem>
          <Form.FormItem label="是否中标" name="alreadyUse" rules={[{ required: true, message: '请选择是否中标' }]}>
            <Select placeholder="请选择是否中标">
              <Select.Option value="1">是</Select.Option>
              <Select.Option value="0">否</Select.Option>
            </Select>
          </Form.FormItem>
          <Form.FormItem label="投标活跃度" name="tenderVal" rules={[{ required: true, message: '请输入投标活跃度' }]}>
            <Input placeholder="请输入投标活跃度" />
          </Form.FormItem>
          <Form.FormItem label="主营范围" name="mainBusinessScope" rules={[{ required: true, message: '请输入主营范围' }]}>
            <Input placeholder="请输入主营范围" />
          </Form.FormItem>
          <Form.FormItem label="供应商类型" name="type" rules={[{ required: true, message: '请选择供应商类型' }]}>
            <Select placeholder="请选择供应商类型">
              <Select.Option value="生产商">生产商</Select.Option>
              <Select.Option value="代理商">代理商</Select.Option>
              <Select.Option value="其他">其他</Select.Option>
            </Select>
          </Form.FormItem>
          <Form.FormItem label="入库方式" name="storageMethod" rules={[{ required: true, message: '请选择入库方式' }]}>
            <Select placeholder="请选择入库方式">
              <Select.Option value="自主寻源">自主寻源</Select.Option>
              <Select.Option value="推荐引入">推荐引入</Select.Option>
              <Select.Option value="公开征集">公开征集</Select.Option>
            </Select>
          </Form.FormItem>
          <Form.FormItem label="入库时间" name="storageTime" rules={[{ required: true, message: '请选择入库时间' }]}>
            <DatePicker
              mode="date"
              presetsPlacement="bottom"
            />
          </Form.FormItem>
          <Form.FormItem label="适用范围" name="applicableScope" rules={[{ required: true, message: '请选择适用范围' }]}>
            <Select placeholder="请选择适用范围">
              <Select.Option value="本级">本级</Select.Option>
              <Select.Option value="本级及下级">本级及下级</Select.Option>
            </Select>
          </Form.FormItem>
        </Form>
      </Dialog>

      {/* 导入供应商弹窗 */}
      <Dialog
        header="导入供应商"
        visible={importDialogVisible}
        onClose={() => setImportDialogVisible(false)}
        onConfirm={handleImportSubmit}
        confirmBtn="导入"
        width={800}
      >
        <div className="space-y-6">
          {/* 模板下载区域 */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">下载导入模板</p>
                <p className="text-xs text-gray-500">请先下载模板，按照模板格式填写数据后再导入</p>
              </div>
              <Button icon={<DownloadIcon />} onClick={handleDownloadTemplate}>下载模板</Button>
            </div>
            <div className="mt-3 border border-gray-200 rounded overflow-hidden">
              <img src={importTemplate} alt="导入模板" className="w-full h-auto" />
            </div>
          </div>

          {/* 文件上传区域 */}
          <div className="p-4 border border-gray-200 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-3">上传文件</p>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleImportFileChange}
              className="hidden"
              id="import-file-input"
            />
            <label
              htmlFor="import-file-input"
              className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-200 rounded-lg hover:border-[#1c5bff] hover:bg-[#1c5bff]/5 transition-colors cursor-pointer"
            >
              {importFile ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded bg-blue-100 flex items-center justify-center">
                    <UploadIcon className="w-5 h-5 text-blue-500" />
                  </div>
                  <span className="text-sm text-gray-700">{importFile.name}</span>
                </div>
              ) : (
                <>
                  <UploadIcon className="w-8 h-8 text-gray-400" />
                  <span className="text-sm text-gray-400 mt-2">点击或拖拽上传文件</span>
                  <span className="text-xs text-gray-400 mt-1">支持CSV、XLS、XLSX格式，单个不超过10MB</span>
                </>
              )}
            </label>
          </div>
        </div>
      </Dialog>

      {/* 导入结果弹窗（显示错误列表） */}
      <Dialog
        header="导入结果"
        visible={errorDialogVisible}
        onClose={() => setErrorDialogVisible(false)}
        confirmBtn="关闭"
        width={800}
      >
        <div className="mb-4">
          <p className="text-gray-700 mb-2">导入完成，但存在以下问题：</p>
          <div className="flex gap-4 text-sm">
            <span className="text-green-600">成功：{importStats.successCount} 条</span>
            <span className="text-red-600">失败：{importStats.failCount} 条</span>
          </div>
          {importStats.successCount > 0 && (
            <div className="mt-2">
              <span className="text-sm text-red-600 cursor-pointer">注意：由于有成功数据在里面，所以请将成功数据删掉，并参考失败原因修改失败数据的格式，再重新上传，以免造成数据重复！！！</span>
            </div>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg p-4">
          <ul className="space-y-2">
            {importErrors.map((error, index) => (
              <li key={index} className="text-sm text-red-600">
                {error}
              </li>
            ))}
          </ul>
        </div>
      </Dialog>
    </div>
  );
}